import { useState, useMemo, useCallback } from "react";

export type Order = "asc" | "desc";

export function stableSort<T>(array: T[], cmp: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = cmp(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getSorting<K extends keyof any>(
  order: Order,
  orderBy: K
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): (a: { [key in K]?: any }, b: { [key in K]?: any }) => number {
  return order === "desc" ? (a, b) => desc(a, b, orderBy) : (a, b) => -desc(a, b, orderBy);
}

export function desc<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

export type SortHandler = (event: React.MouseEvent<unknown, MouseEvent>) => void;

export interface SortOrderType {
  active: boolean;
  order: Order;
  onSort?: SortHandler;
}

export type SortOrderProps<K> = {
  [key in keyof K]?: SortOrderType;
};

export const useSort = <K>(
  initialSortBy: keyof K,
  sortableProperties: Array<keyof K>
): [SortOrderProps<K>, (items: K[]) => K[]] => {
  const defaultSortDirection = "asc";
  const [sortBy, setSortBy] = useState<keyof K>(initialSortBy);
  const [sortDirection, setSortDirection] = useState<Order>(defaultSortDirection);

  const sortOrder = useMemo(() => {
    return sortableProperties.reduce(
      (props: SortOrderProps<K>, header: keyof K) => ({
        ...props,
        [header]: {
          active: sortBy === header,
          order: sortBy === header ? sortDirection : defaultSortDirection,
          onSort: () => {
            const currentSort = sortBy === header;
            const invertDirection = sortDirection === "asc" ? "desc" : "asc";
            setSortDirection(currentSort ? invertDirection : defaultSortDirection);
            setSortBy(header);
          },
        },
      }),
      {}
    );
  }, [sortBy, sortDirection, sortableProperties]);

  const sort = useCallback(
    (items: K[]): K[] => {
      return stableSort(items, getSorting(sortDirection, sortBy));
    },
    [sortDirection, sortBy]
  );

  return [sortOrder, sort];
};
